/**
 * Embedded Felt map for European price viewer
 */

import { useEffect, useRef, useState } from 'react';
import type { FeltController } from '@feltmaps/js-sdk';
import { Felt } from '@feltmaps/js-sdk';

interface EuroPriceMapProps {
  mapId?: string;
}

const EuroPriceMap: React.FC<EuroPriceMapProps> = ({ mapId }) => {
  const [feltController, setFeltController] = useState<FeltController | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    async function loadFelt() {
      if (hasLoadedRef.current) return;
      if (!mapContainerRef.current) return;
      if (!mapId) {
        console.warn('No Felt map ID provided');
        return;
      }

      hasLoadedRef.current = true;

      try {
        const controller = await Felt.embed(mapContainerRef.current, mapId, {
          uiControls: {
            cooperativeGestures: false,
            fullScreenButton: true,
            showLegend: false,
          },
        });
        setFeltController(controller);
      } catch (error) {
        console.error('Failed to load Felt map:', error);
      }
    }

    loadFelt();

    // Cleanup: Remove the iframe when component unmounts
    return () => {
      if (feltController?.iframe) {
        feltController.iframe.remove();
      }
    };
  }, [mapId, feltController]);

  if (!mapId) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          border: '1px solid #e0e0e0',
          color: '#666',
          fontSize: '14px',
        }}
      >
        No map ID provided. Set a Felt map ID to view the map.
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        border: '1px solid #e0e0e0',
      }}
    />
  );
};

export default EuroPriceMap;

